import { Col, Row } from "antd";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useSessionStore } from "../../../../store/session-store.js";
import { useAxiosPrivate } from "../../../../hooks/useAxiosPrivate";
import { ReviewHeader } from "../../components/review-header/ReviewHeader.jsx";
import { IslandLayout } from "../../../../layouts/island-layout/IslandLayout.jsx";
import { DocumentViewer } from "../../../../components/custom-tools/document-viewer/DocumentViewer.jsx";
import { PdfViewer } from "../../../../components/custom-tools/pdf-viewer/PdfViewer.jsx";
import "../../../../components/custom-tools/tool-ide/ToolIde.css";
import { ResultEditor } from "../../components/result-editor/ResultEditor.jsx";
import { EmptyState } from "../../../../components/widgets/empty-state/EmptyState.jsx";
import { useAlertStore } from "../../../../store/alert-store";
import { useExceptionHandler } from "../../../../hooks/useExceptionHandler";
import { ImageHighlighter } from "../../../image-highlighter/ImageHighlighter.jsx";

import "../ManualReviewPage.css";

function SimpleManualReviewPage({ type }) {
  const { sessionDetails } = useSessionStore();
  const axiosPrivate = useAxiosPrivate();
  const [classList, setClassList] = useState([]);
  const [currentReview, setCurrentReview] = useState();
  const [reviewMode, setReviewMode] = useState();
  const [processedData, setProcessedData] = useState({});
  const [currentClass, setCurrentClass] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState("");
  const [queueData, setQueueData] = useState({});
  const { setAlertDetails } = useAlertStore();
  const [highlightMetadata, setHighlightMetadata] = useState({});
  const [highlightData, setHighlightData] = useState([]);
  const handleException = useExceptionHandler();
  const path = `/api/v1/unstract/${sessionDetails.orgId.replaceAll(
    '"',
    ""
  )}/manual_review`;
  useEffect(() => {
    if (type === "simple_review") {
      setReviewMode("review");
    } else if (type === "simple_approve") {
      setReviewMode("approve");
    }
    setCurrentReview(null);
    const option = {
      url: `${path}/document_classes_api/`,
      method: "GET",
    };
    axiosPrivate(option)
      .then((result) => {
        setClassList(result?.data?.data);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to get document classes"));
      });
  }, []);

  useEffect(() => {
    setCurrentReview(null);
  }, [currentClass, type]);

  const updateCurrentReview = (result) => {
    // Check if the data is already parsed or needs parsing
    const rawData = result?.data?.data;
    // If rawData is a string and looks like it contains JSON, we parse it
    let parsedData;
    if (typeof rawData === "string") {
      try {
        parsedData = JSON.parse(rawData);
      } catch (parseError) {
        parsedData = rawData; // Fallback to raw data if parsing fails
      }
    } else {
      parsedData = rawData; // If rawData is already an object
    }
    setQueueData(parsedData);
    setFileUrl(parsedData?.file_url);
    setCurrentReview(parsedData?.result); // Set the parsed data
    setHighlightMetadata(parsedData?.result?.metadata?.highlight_data || {});
    setFileName(parsedData?.file);
    setFileType(parsedData?.fileType);
    const tableData = parsedData?.result?.output;
    setProcessedData(tableData);
  };

  const fetchNextApi = () => {
    setHighlightData([]);
    const option = {
      url: `${path}/next_api/?class_name=${currentClass}&type=${reviewMode}`,
      method: "GET",
    };
    axiosPrivate(option)
      .then((result) => {
        try {
          updateCurrentReview(result);
        } catch (error) {
          console.error("Error handling response data:", error); // Log any error
        }
      })
      .catch((err) => {
        setAlertDetails(handleException(err, err.response.data.error));
      });
  };

  const updateEditedData = () => {
    setAlertDetails({
      type: "success",
      content: "Succesfully updated the data.",
    });
  };

  const finishReviewApi = () => {
    const body = {
      updated_data: queueData,
      class_name: currentClass,
      type: reviewMode,
    };
    const option = {
      url: `${path}/finish_review_api/`,
      method: "POST",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: body,
    };
    axiosPrivate(option)
      .then((result) => {
        setCurrentReview(null);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, err.response.data.error));
      });
  };
  const flattenToArrayOfArrays = (data) => {
    const result = [];
    const flatten = (item) => {
      if (
        Array.isArray(item) &&
        item.every((el) => Array.isArray(el) && el.length === 4)
      ) {
        result.push(...item);
      } else if (Array.isArray(item)) {
        item.forEach(flatten);
      } else if (item && typeof item === "object") {
        for (const key in item) {
          if (Object.hasOwn(item, key)) {
            flatten(item[key]);
          }
        }
      }
    };
    if (Array.isArray(data) && data.length > 0 && !Array.isArray(data[0])) {
      data = [data];
    }
    flatten(data);
    return result;
  };
  const highlightText = (path) => {
    let newHighlightData = [];
    const target = path.reduce((current, key) => {
      const next = current?.[key];
      if (next) {
        return next;
      }
      if (Array.isArray(current) && current.length === 1) {
        return current[0]?.[key];
      }
      return null;
    }, highlightMetadata);
    if (target) {
      newHighlightData = flattenToArrayOfArrays(target);
    }
    setHighlightData(newHighlightData);
  };
  return (
    <>
      <ReviewHeader
        title={fileName}
        classList={classList}
        currentReview={currentReview}
        setCurrentClass={setCurrentClass}
        currentClass={currentClass}
        sessionDetails={sessionDetails}
        type={type}
        fetchNextApi={fetchNextApi}
        finishReviewApi={finishReviewApi}
      />
      <div className="user-bg-col">
        <IslandLayout>
          {currentReview && (
            <Row className="tool-ide-main">
              <Col span={12} className="tool-ide-col review-queue-col">
                <div className="tool-ide-prompts review-queue-div">
                  <DocumentViewer
                    doc={fileName}
                    isLoading={false}
                    isContentAvailable={fileUrl?.length > 0}
                    errMsg={"Failed to load file"}
                  >
                    {fileType === "pdf" && (
                      <PdfViewer
                        fileUrl={fileUrl}
                        highlightData={highlightData}
                      />
                    )}
                    {fileType !== "pdf" && (
                      <ImageHighlighter
                        imageSrc={fileUrl}
                        highlightData={highlightData}
                      />
                    )}
                  </DocumentViewer>
                </div>
              </Col>
              <Col span={12} className="tool-ide-col review-queue-col">
                <div className="tool-ide-prompts review-queue-div">
                  <ResultEditor
                    data={processedData}
                    queueData={queueData}
                    setQueueData={setQueueData}
                    updateEditedData={updateEditedData}
                    type={type}
                    highlightText={highlightText}
                  />
                </div>
              </Col>
            </Row>
          )}
          {!currentReview && <EmptyState text="Select class and fetch next" />}
        </IslandLayout>
      </div>
    </>
  );
}

SimpleManualReviewPage.propTypes = {
  type: PropTypes.string,
};

export { SimpleManualReviewPage };
